import { useEffect, useMemo, useState } from 'react';
import {
  CreditCard,
  Eye,
  EyeOff,
  Save,
  Trash2,
  CheckCircle,
  AlertCircle,
  Copy,
  ExternalLink,
  TestTube,
  Zap,
} from 'lucide-react';
import api from '@/lib/api';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Card from '@/components/ui/Card';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

/**
 * Tenant-facing payments admin. Focused page for Stripe + PayPal credentials,
 * with a webhook URL display, live/test mode indicator, and a test-connection
 * button per provider. Backs onto the shared /admin/credentials API + the
 * credential_overrides table.
 */

interface Credential {
  key: string;
  label: string;
  category: string;
  source: 'override' | 'platform' | 'none';
  masked_value: string | null;
  is_set: boolean;
}

interface PaymentsInfo {
  webhook_url: string;
  stripe_mode: 'test' | 'live' | 'unset';
  paypal_mode: 'sandbox' | 'live' | 'unset';
}

interface TestResult {
  ok: boolean;
  message?: string;
  account_id?: string;
  account_name?: string | null;
  livemode?: boolean;
  mode?: string;
  error?: string;
}

const STRIPE_KEYS = ['STRIPE_SECRET_KEY', 'STRIPE_PUBLISHABLE_KEY', 'STRIPE_WEBHOOK_SECRET'] as const;
const PAYPAL_KEYS = ['PAYPAL_CLIENT_ID', 'PAYPAL_CLIENT_SECRET'] as const;

const KEY_HELP: Record<string, string> = {
  STRIPE_SECRET_KEY:
    "Stripe Secret Key (sk_test_… / sk_live_…) from Developers → API keys — simplest to start. If you prefer a Restricted Key, grant write access to Products, Prices, Checkout sessions, Customers, Subscriptions, Invoices, Coupons, and Promotion codes (subscription sync + promo codes need these).",
  STRIPE_PUBLISHABLE_KEY:
    "Stripe Publishable Key (pk_test_… / pk_live_…) from the same API keys page. Safe to expose — the checkout and booking forms load it from the site's public settings, so no rebuild is needed after you paste it.",
  STRIPE_WEBHOOK_SECRET:
    "Stripe Webhook Signing Secret (whsec_…) — Developers → Webhooks → your endpoint → Signing secret. Paste the webhook URL above into your Stripe dashboard if you haven't already.",
  PAYPAL_CLIENT_ID: "PayPal app Client ID — developer.paypal.com → My Apps → your app.",
  PAYPAL_CLIENT_SECRET: "PayPal app Client Secret. Treat as a password.",
};

function ModeBadge({ mode }: { mode: 'test' | 'live' | 'sandbox' | 'unset' }) {
  if (mode === 'unset')
    return (
      <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-500">
        Not configured
      </span>
    );
  if (mode === 'live')
    return (
      <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
        Live mode
      </span>
    );
  return (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
      {mode === 'sandbox' ? 'Sandbox' : 'Test mode'}
    </span>
  );
}

function SourceBadge({ source }: { source: Credential['source'] }) {
  if (source === 'override')
    return <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">This site</span>;
  if (source === 'platform')
    return <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600">Platform default</span>;
  return <span className="rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-500">Not set</span>;
}

export default function PaymentsSettings() {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [info, setInfo] = useState<PaymentsInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [showValue, setShowValue] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedKey, setSavedKey] = useState<string | null>(null);
  const [testingStripe, setTestingStripe] = useState(false);
  const [testingPaypal, setTestingPaypal] = useState(false);
  const [stripeTest, setStripeTest] = useState<TestResult | null>(null);
  const [paypalTest, setPaypalTest] = useState<TestResult | null>(null);
  const [copied, setCopied] = useState(false);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const [credRes, infoRes] = await Promise.all([
        api.get<{ credentials: Credential[] }>('/admin/credentials'),
        api.get<PaymentsInfo>('/admin/payments/info'),
      ]);
      setCredentials(
        credRes.credentials.filter((c) => c.category === 'payments'),
      );
      setInfo(infoRes);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load payments settings');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const byKey = useMemo(() => {
    const m = new Map<string, Credential>();
    for (const c of credentials) m.set(c.key, c);
    return m;
  }, [credentials]);

  async function handleSave(key: string) {
    if (!editValue.trim()) return;
    setSaving(true);
    try {
      await api.put(`/admin/credentials/${key}`, { value: editValue.trim() });
      setEditingKey(null);
      setEditValue('');
      setShowValue(false);
      setSavedKey(key);
      window.setTimeout(() => setSavedKey(null), 2000);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save credential');
    } finally {
      setSaving(false);
    }
  }

  async function handleClear(key: string) {
    setSaving(true);
    try {
      await api.delete(`/admin/credentials/${key}`);
      await load();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to clear credential');
    } finally {
      setSaving(false);
    }
  }

  async function testStripe() {
    setTestingStripe(true);
    setStripeTest(null);
    try {
      const res = await api.post<TestResult>('/admin/payments/test/stripe', {});
      setStripeTest(res);
    } catch (err: unknown) {
      setStripeTest({ ok: false, message: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTestingStripe(false);
    }
  }

  async function testPaypal() {
    setTestingPaypal(true);
    setPaypalTest(null);
    try {
      const res = await api.post<TestResult>('/admin/payments/test/paypal', {});
      setPaypalTest(res);
    } catch (err: unknown) {
      setPaypalTest({ ok: false, message: err instanceof Error ? err.message : 'Test failed' });
    } finally {
      setTestingPaypal(false);
    }
  }

  function copyWebhook() {
    if (!info) return;
    navigator.clipboard.writeText(info.webhook_url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  }

  function renderCredentialRow(key: string) {
    const cred = byKey.get(key);
    const label = cred?.label ?? key;
    const source = cred?.source ?? 'none';
    const masked = cred?.masked_value ?? null;
    const help = KEY_HELP[key];
    const isEditing = editingKey === key;

    return (
      <div key={key} className="py-3 first:pt-0 last:pb-0">
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-text-primary">{label}</span>
              <SourceBadge source={source} />
              {savedKey === key && <CheckCircle className="h-4 w-4 text-green-500" />}
            </div>
            {help && <p className="mt-1 text-xs text-text-muted">{help}</p>}
            <div className="mt-1 font-mono text-xs text-text-muted">
              {masked ?? 'Not configured'}
            </div>
          </div>

          {!isEditing && (
            <div className="flex shrink-0 items-center gap-1">
              <Button
                size="sm"
                variant="boutique-outline"
                onClick={() => {
                  setEditingKey(key);
                  setEditValue('');
                  setShowValue(false);
                }}
              >
                {cred?.is_set ? 'Replace' : 'Set'}
              </Button>
              {source === 'override' && (
                <Button
                  size="sm"
                  variant="boutique-outline"
                  onClick={() => handleClear(key)}
                  disabled={saving}
                  title="Remove this site's override (falls back to platform default)"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          )}
        </div>

        {isEditing && (
          <div className="mt-2 flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                type={showValue ? 'text' : 'password'}
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder={`Paste your ${label} here`}
                autoFocus
                className="pr-9 text-sm"
              />
              <button
                type="button"
                onClick={() => setShowValue(!showValue)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-secondary"
              >
                {showValue ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button size="sm" variant="boutique" onClick={() => handleSave(key)} disabled={saving || !editValue.trim()} isLoading={saving}>
              <Save className="h-3.5 w-3.5" /> Save
            </Button>
            <Button size="sm" variant="boutique-outline" onClick={() => setEditingKey(null)} disabled={saving}>
              Cancel
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-text-primary">
          <CreditCard className="h-6 w-6" />
          Payment Providers
        </h1>
        <p className="mt-1 text-sm text-text-secondary">
          Connect your Stripe and PayPal accounts. Keys are stored encrypted; the page never displays the full value back.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">
            &times;
          </button>
        </div>
      )}

      <Card>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-text-primary">
              Stripe <ModeBadge mode={info?.stripe_mode ?? 'unset'} />
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              Card payments, subscriptions, and Apple/Google Pay. Recommended for most stores.
            </p>
          </div>
          <Button
            size="sm"
            variant="boutique-outline"
            onClick={testStripe}
            disabled={testingStripe || info?.stripe_mode === 'unset'}
            isLoading={testingStripe}
          >
            <TestTube className="h-3.5 w-3.5" /> Test connection
          </Button>
        </div>

        {stripeTest && (
          <div
            className={`mt-3 rounded-md border p-3 text-sm ${
              stripeTest.ok
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {stripeTest.ok ? (
              <div className="flex items-start gap-2">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <div className="font-medium">
                    Connected to {stripeTest.account_name ?? stripeTest.account_id}
                  </div>
                  <div className="text-xs">
                    Account <span className="font-mono">{stripeTest.account_id}</span> —{' '}
                    {stripeTest.livemode ? 'live mode' : 'test mode'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <div className="font-medium">Stripe rejected the key</div>
                  <div className="text-xs">{stripeTest.message}</div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 divide-y divide-stone-100">
          {STRIPE_KEYS.map(renderCredentialRow)}
        </div>

        {info && (
          <div className="mt-4 rounded-md border border-stone-200 bg-stone-50 p-3">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-xs font-medium uppercase tracking-wide text-text-muted">
                  Webhook URL
                </div>
                <code className="block truncate text-xs text-text-secondary">
                  {info.webhook_url}
                </code>
              </div>
              <Button size="sm" variant="boutique-outline" onClick={copyWebhook}>
                <Copy className="h-3.5 w-3.5" />
                {copied ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="mt-2 text-xs text-text-muted">
              Paste this into Stripe → Developers → Webhooks → Add endpoint. Subscribe to{' '}
              <code>checkout.session.completed</code>, <code>customer.subscription.created</code>,{' '}
              <code>customer.subscription.updated</code>, <code>customer.subscription.deleted</code>, and{' '}
              <code>invoice.paid</code>. After saving, copy the resulting signing secret
              into <strong>Stripe Webhook Secret</strong> above.{' '}
              <a
                className="text-stone-600 underline hover:text-stone-800"
                href="https://docs.stripe.com/webhooks"
                target="_blank"
                rel="noreferrer"
              >
                Docs <ExternalLink className="inline h-3 w-3" />
              </a>
            </p>
          </div>
        )}
      </Card>

      <Card>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="flex items-center gap-2 text-lg font-semibold text-text-primary">
              PayPal <ModeBadge mode={info?.paypal_mode ?? 'unset'} />
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              PayPal + Venmo checkout. Optional — only customers who specifically prefer it use this rail.
            </p>
          </div>
          <Button
            size="sm"
            variant="boutique-outline"
            onClick={testPaypal}
            disabled={testingPaypal || info?.paypal_mode === 'unset'}
            isLoading={testingPaypal}
          >
            <TestTube className="h-3.5 w-3.5" /> Test connection
          </Button>
        </div>

        {paypalTest && (
          <div
            className={`mt-3 rounded-md border p-3 text-sm ${
              paypalTest.ok
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {paypalTest.ok ? (
              <div className="flex items-start gap-2">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <div className="font-medium">PayPal credentials valid</div>
                  <div className="text-xs">{paypalTest.mode === 'live' ? 'Live mode' : 'Sandbox mode'}</div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>
                  <div className="font-medium">PayPal rejected the credentials</div>
                  <div className="text-xs">{paypalTest.message}</div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="mt-4 divide-y divide-stone-100">
          {PAYPAL_KEYS.map(renderCredentialRow)}
        </div>
      </Card>

      <div className="rounded-lg border border-stone-200 bg-stone-50 p-4 text-xs text-text-muted">
        <div className="mb-1 flex items-center gap-1 font-medium text-text-secondary">
          <Zap className="h-3.5 w-3.5" /> Tips
        </div>
        <ul className="space-y-1 pl-4 [list-style:disc]">
          <li>Start in test/sandbox mode. Stripe's test card <code>4242 4242 4242 4242</code> goes through end-to-end without charging anyone.</li>
          <li>Restricted Stripe keys (<code>rk_test_…</code> / <code>rk_live_…</code>) are safer than secret keys — they're scoped to specific permissions.</li>
          <li>Switching modes? Update both keys at once. Stripe rejects requests mixing test + live keys.</li>
        </ul>
      </div>
    </div>
  );
}
